import { ColumnState } from 'ag-grid-community'
import { ROW_HEIGHT } from '../../../view/containers/BulkSheet'

// マイタスクモック画面（demo-pages/MyTasks.tsx）のUIState初期値。
// functionUuidは画面側と揃える必要があるため、ここを単一の定義元にして
// MyTasks.tsxからimportする。
export const MY_TASKS_MOCK_FUNCTION_UUID = 'mock-my-tasks-function-uuid'

// usePageState（MyWbsItems/hooks/pageState.ts）が復元する表示状態。
// 「表示形式_行の高さ」を既定のSMALL(32)から一段階上のMEDIUM(50)にして、
// 初期表示から行に余裕がある状態で見せる。
//
// 復元されるのはpayloadに載せたキーだけで、載せなかったものは
// usePageStateのuseState初期値（workloadUnit=HOUR等）がそのまま残る。
// 行の高さ以外は既定のままにしたいので、rowHeightのみを指定する。
export const myTasksDefaultPageState = {
  rowHeight: ROW_HEIGHT.MEDIUM,
}

// タスクタブで表示したい列。widthはデモデータが見切れないよう既定から
// 調整した実測値（undefinedの列は列定義の幅のまま）。最長値ではなく
// 90パーセンタイルに合わせ、極端に長い行は見切れてよいものとする。
// 全部を収めると右端の投下工数列が画面外に出るため。
//
// 投下工数の日付列はcolIdが日付（YYYY-MM-DD）で日替わりするため列挙できない。
// ColumnStateに無い列はapplyColumnStateが触らないので既定のまま残る。
const MY_TASKS_VISIBLE_COLUMNS: { colId: string; width?: number }[] = [
  { colId: 'body.wbsItem.status' }, // ステータス
  { colId: 'body.project', width: 180 }, // プロジェクト
  // 親WBS列はfield='body.parentWbsItem'でcolIdを持たないので、colIdはfieldと同じ。
  { colId: 'body.parentWbsItem', width: 270 }, // 親WBS
  // 既定400pxは実データに対し広すぎるので、親WBSを広げた分をここから回す。
  { colId: 'body.wbsItem.displayName', width: 220 },
  { colId: 'body.wbsItem.scheduledDate.startDate' }, // 予定開始日
  { colId: 'body.wbsItem.scheduledDate.endDate' }, // 予定終了日
  { colId: 'body.wbsItem.estimatedHour.task' }, // 見積_タスク
  { colId: 'body.wbsItem.actualHour' }, // 生産性_投下工数
]

// 既定で表示される列のうち、上の一覧に無いもの。applyColumnStateは
// ColumnStateに現れない列を触らないため、隠したい列は明示的に挙げる必要がある。
const MY_TASKS_HIDDEN_COL_IDS = [
  'action', // コメント（デモではコメント機能を見せない）
  'body.tags', // タグ
  'body.wbsItem.description', // 説明
  'body.commentSummary.latestComment', // 最新コメント
  'body.wbsItem.team', // 担当_チーム
  'body.wbsItem.accountable', // 担当_責任者
  'body.wbsItem.responsible', // 担当_取りまとめ担当
  'body.wbsItem.watchers', // 担当_ウォッチャー
  'body.wbsItem.sprint', // 期間_スプリント
]

// useBulkSheetStateが読む列状態。キーは
// `BULK_SHEET_STATE-MY_WBS_ITEMS_TASK_TAB`（stateKeyの組み立て規則）。
//
// 並び順は指定できない。applyColumnStateの呼び出しが applyOrder: false
// （MyWbsItems/index.tsx）なので、ColumnStateのorderは無視され、列定義
// （gridOptions/defaultColumns.ts）の順序がそのまま使われる。
// マイタスクは3タブが同じfunctionUuidで列状態を引き、keyの末尾だけが
// タブ名で変わる（useBulkSheetStateのstateKey = `BULK_SHEET_STATE-<タブ名>`）。
// 接頭辞だけを見る共通のisBulkSheetStateUiStateKeyではタブを区別できず、
// タスクタブ用の列状態を成果物・ウォッチャータブまで奪ってしまうため、
// タスクタブのkeyに完全一致で判定する。
const MY_WBS_ITEMS_TASK_TAB_UI_STATE_KEY =
  'BULK_SHEET_STATE-MY_WBS_ITEMS_TASK_TAB'

export const isMyWbsItemsTaskTabUiStateKey = (key: string | null): boolean =>
  key === MY_WBS_ITEMS_TASK_TAB_UI_STATE_KEY

export const myTasksTaskTabColumnState: ColumnState[] = [
  ...MY_TASKS_VISIBLE_COLUMNS.map(({ colId, width }) => ({
    colId,
    hide: false,
    // widthがundefinedの列はapplyColumnStateが幅を触らず、列定義の値が残る。
    ...(width ? { width } : {}),
  })),
  ...MY_TASKS_HIDDEN_COL_IDS.map(colId => ({ colId, hide: true })),
]
